import React, { useContext, useState } from "react";
import { Button, Col, Modal, ModalProps, Row } from "antd";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import styles from "src/styles";

// Contexts
import { OffsetLayoutContext } from "src/contexts/layout.context";

// Helpers
import { calculateLayout } from "src/helpers/ui.helper";

// Components
import CarSearchCard, {
    CarSearchForm,
} from "src/components/booking/CarSearchCard";

// Assets
import backgroundImage from "src/assets/images/find_car_background.png";
import { ReactComponent as ClockIcon } from "src/assets/icons/clock.svg";
import { ReactComponent as MarkCircleIcon } from "src/assets/icons/mark-circle.svg";
import { ReactComponent as CarInsuranceIcon } from "src/assets/icons/car-insurance.svg";
import { ReactComponent as CarGuaranteeIcon } from "src/assets/icons/car-guarantee.svg";
import { ReactComponent as PriceTagIcon } from "src/assets/icons/price-tag.svg";

type Props = {
    locationOptions?: SelectOption[];
    fetchAddressOptions: (value?: string) => Promise<SelectOption[]>;
    onClick?: (values: CarSearchForm) => void;
};

const FindCarBanner = ({
    locationOptions = [],
    fetchAddressOptions,
    onClick = () => {},
}: Props) => {
    const offsetLayout = useContext(OffsetLayoutContext);

    const [openModal, setOpenModal] = useState<boolean>(false);

    const layout = calculateLayout(offsetLayout, 24);

    return (
        <>
            <DivStyled
                className="find-car-banner"
                backgroundImg={backgroundImage}
            >
                <Row className="content">
                    <Col {...layout}>
                        <div className="title">
                            <FormattedMessage id="findCarBanner.title" />
                        </div>
                        <div className="profit">
                            <div className="icon">
                                <ClockIcon />
                            </div>
                            <FormattedMessage id="findCarBanner.profit1" />
                        </div>
                        <div className="profit">
                            <div className="icon">
                                <MarkCircleIcon />
                            </div>
                            <FormattedMessage id="findCarBanner.profit2" />
                        </div>
                        <div className="profit">
                            <div className="icon">
                                <CarInsuranceIcon />
                            </div>
                            <FormattedMessage id="findCarBanner.profit3" />
                        </div>
                        <div className="profit">
                            <div className="icon">
                                <CarGuaranteeIcon />
                            </div>
                            <FormattedMessage id="findCarBanner.profit4" />
                        </div>
                        <div className="profit">
                            <div className="icon">
                                <PriceTagIcon />
                            </div>
                            <FormattedMessage id="findCarBanner.profit5" />
                        </div>
                        <div className="actions">
                            <Button
                                type="primary"
                                className="reserve-button"
                                onClick={() => setOpenModal(true)}
                            >
                                <FormattedMessage id="findCarBanner.actionButton" />
                            </Button>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col xs={24} md={0} className="images">
                        <img src={backgroundImage} alt="background" />
                    </Col>
                </Row>
            </DivStyled>

            <ModalStyled
                className="car-search-modal"
                centered={true}
                closable={false}
                destroyOnClose={true}
                footer={false}
                maskClosable={true}
                visible={openModal}
                width="80%"
                onCancel={() => setOpenModal(false)}
            >
                <CarSearchCard
                    formName="findCarModal"
                    locationOptions={locationOptions}
                    fetchAddressOptions={fetchAddressOptions}
                    onSubmit={(values) => onClick(values)}
                />
            </ModalStyled>
        </>
    );
};

const DivStyled = styled.div<
    React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
    > & { backgroundImg: string }
>`
    &.find-car-banner {
        background: url(${(props) => props.backgroundImg});
        background-repeat: no-repeat;
        background-position: right;
        // height: 505px;

        @media screen and (max-width: 767px) {
            background: #112a50;
        }

        .content {
            padding: 100px 0 40px;
            background: linear-gradient(
                89.95deg,
                #112a50 30.33%,
                rgba(17, 42, 80, 0) 91.26%
            );
            height: 100%;

            @media screen and (max-width: 767px) {
                padding-top: 80px;
                padding-bottom: 0;
            }
        }

        .images {
            margin-top: 24px;

            img {
                width: 100%;
                -webkit-mask-image: linear-gradient(
                    0deg,
                    #112a50 55%,
                    rgba(1, 17, 64, 0) 100%
                );
            }
        }

        .title {
            font-style: normal;
            font-weight: 400;
            font-size: 31px;
            line-height: 50px;
            color: ${styles.fontColorWhite};
            margin-bottom: 20px;
        }

        .profit {
            display: flex;
            align-items: center;
            font-style: normal;
            font-weight: 400;
            font-size: 18px;
            line-height: 29px;
            color: ${styles.fontColorLightGrayLv2};
            margin-top: 9px;

            div.icon {
                display: flex;
                margin-right: 24px;

                svg {
                    width: 37px;
                }
            }
        }

        .actions {
            margin-top: 110px;

            @media screen and (max-width: 991px) {
                margin-top: 70px;
            }

            @media screen and (max-width: 575px) {
                margin-top: 50px;
            }

            .reserve-button {
                width: 180px;
                height: 44px;
                font-style: normal;
                font-weight: 600;
                font-size: 20px;
                line-height: 32px;
                color: ${styles.fontColorWhite};
            }
        }
    }
`;

const ModalStyled = styled(Modal)<ModalProps & { children: React.ReactChild }>`
    &.car-search-modal {
        .ant-modal-body {
            padding: 0;
        }
    }
`;

export default FindCarBanner;
